// K6 Phase 4 — the batch wizard for BALL MACHINE vouchers, shared by two audiences:
//   mode 'platform' → super admin (any funding split, scope by devices or sites)
//   mode 'tenant'   → the court's own Vouchers page (court-funded only, its linked phones only)
// The server computes every discount at claim time; this only describes the batch.
import { useMemo, useState } from 'react';
import { X, Copy, CheckCircle, ChevronRight, ChevronLeft, Percent, Clock, Wallet, Timer, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { RED, GREEN, GREY, AMBER, inputStyle, labelStyle, ModalShell } from './KioskDialogs';
import { errMsg, ymd } from './kioskApi';

export const KIOSK_VOUCHER_TYPES = [
  { id: 'flat', label: 'Pesos off', desc: '₱ off the booked price', Icon: Wallet },
  { id: 'percent', label: 'Percent off', desc: '% off the booked price', Icon: Percent },
  { id: 'free_time', label: 'Free minutes', desc: 'Minutes at the session’s own rate', Icon: Clock },
  // K6 P4c — the fourth type: minutes of FREE MACHINE TIME, never a peso discount.
  // HighMark only (owner, 2026-09-30): free machine minutes cost HighMark, and a court's vouchers
  // may only cost the court - the server refuses it from a court too.
  { id: 'bonus_time', label: 'Free machine time', desc: 'Extra minutes on the clock, at no charge', Icon: Timer, platformOnly: true },
  // 2026-09-30 — a FREE TRIAL: "someone wants to try the machine". N minutes (5–60) at ₱0, no paid
  // booking behind it, one use. HighMark only; needs the kiosk app that can run one (0.20+).
  { id: 'trial', label: 'Free trial', desc: 'A few minutes to try the machine, ₱0, no booking', Icon: PlayCircle, platformOnly: true },
];

export const TRIAL_MINUTES = [5, 10, 15, 30];
export const TRIAL_FUNDING_LINE = 'A free trial costs nobody anything: ₱0 to the customer, ₱0 to you and the court. It needs the kiosk app 0.20 or newer.';
const MIN_PRESETS = [[30, '30 min'], [60, '1 h'], [90, '1.5 h'], [120, '2 h'], [180, '3 h']];

// K6 P4d — "Who covers the discount?". `shared` is the default for a HighMark-minted
// batch: nobody is charged, each side absorbs the discount in its own share ratio (that
// ratio lives on the phone, so it is a named mode, not a percentage). Only `custom`
// reveals the old slider. A tenant never sees this list — it may only mint `court`.
const FUNDING_CHOICES = [
  { id: 'shared', label: 'Nobody — shared by the usual split', note: 'Each side absorbs it in the same proportion as an ordinary rental. Neither side is charged for the promotion.' },
  { id: 'court', label: 'The court covers it' },
  { id: 'owner', label: 'HighMark covers it' },
  { id: 'custom', label: 'Custom split' },
];

// K6 P4d — `funding` is read FIRST, so a shared batch never shows a percentage.
// A voucher minted before today carries no `funding` at all: it falls through to the
// old percentage wording and reads exactly as it always did (i.e. as a custom split).
export function fundingLabel(v) {
  switch (v?.funding) {
    case 'shared': return 'Shared by the usual split';
    case 'court': return 'Court-funded';
    case 'owner': return 'HighMark-funded';
    default: break; // 'custom' and every older voucher: the percentages below
  }
  const c = Number(v?.court_pct ?? 100);
  if (c >= 100) return 'Court-funded';
  if (c <= 0) return 'HighMark-funded';
  return `Court ${c} % · HighMark ${100 - c} %`;
}

export function scopeLabel(v, deviceNames = []) {
  const d = v?.device_ids || [];
  const s = v?.site_names || [];
  if (!d.length && !s.length) return 'Any kiosk';
  const bits = [];
  if (d.length) bits.push((deviceNames.length ? deviceNames : d).join(', '));
  if (s.length) bits.push(`sites: ${s.join(', ')}`);
  return bits.join(' · ');
}

// K6 P4b — the court-booking gate, worded once for the wizard, both voucher
// lists, the Review page and the statement (the contract fixes the strings).
// Minutes → "3 h" / "1 h 30 min" / "45 min".
export function fmtCourtHours(minutes) {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r} min`;
  return r ? `${h} h ${r} min` : `${h} h`;
}

// "Needs an active court booking of at least 3 h"; '' when the gate is off / not sent yet.
export function courtGateLabel(minutes) {
  const m = Number(minutes) || 0;
  return m > 0 ? `Needs an active court booking of at least ${fmtCourtHours(m)}` : '';
}

// K6 P4c — bonus time. The value is minutes of free machine time, so a bonus voucher
// never shows a peso figure. `bonus_minutes` is the server's field (claim reply, views);
// `value` is what the batch was minted with — either does, and anything else reads as 0.
export function bonusMinutesOf(v) {
  if (!v || v.type !== 'bonus_time') return 0;
  const m = Math.floor(Number(v.bonus_minutes ?? v.value) || 0);
  return m > 0 ? m : 0;
}

// A voucher card: "Free 30 min of machine time"; '' when the voucher is not a bonus.
export function bonusTimeLabel(v) {
  const m = bonusMinutesOf(v);
  return m > 0 ? `Free ${fmtCourtHours(m)} of machine time` : '';
}

// Beside a rental's voucher line on Review and the statements: "+30 min free"; '' when none.
export function bonusFreeLabel(minutes) {
  const m = Math.floor(Number(minutes) || 0);
  return m > 0 ? `+${fmtCourtHours(m)} free` : '';
}

// The funding step does not apply to a bonus: nobody pays for the free time.
export const BONUS_FUNDING_LINE = 'The free time is not charged to either side. You and the court share the money for the paid time as usual.';

// "15:00" → ["3:00", "PM"]; anything unparseable comes back as given.
function h12(hhmm) {
  const [h, mi] = String(hhmm || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(mi)) return [hhmm || '—', ''];
  return [`${h % 12 || 12}:${String(mi).padStart(2, '0')}`, h >= 12 ? 'PM' : 'AM'];
}

// "3:00 – 5:00 PM" (one AM/PM when both share it), "11:30 AM – 1:00 PM" otherwise.
export function fmtCourtBookingTimes(start, end) {
  const [s, sa] = h12(start);
  const [e, ea] = h12(end);
  return sa === ea ? `${s} – ${e}${ea ? ` ${ea}` : ''}` : `${s} ${sa} – ${e} ${ea}`.replace(/\s+/g, ' ').trim();
}

// "Court booking BRN-000123 · Juan Marko · 3:00 – 5:00 PM" from a claimed voucher; '' when none.
// No "#": a booking reference in this system already reads as one (BRN-000123).
export function courtBookingLabel(v) {
  const b = v?.court_booking;
  if (!b || typeof b !== 'object') return '';
  const times = (b.start_time || b.end_time) ? fmtCourtBookingTimes(b.start_time, b.end_time) : null;
  return [`Court booking ${b.ref ?? b.id ?? '—'}`, b.name, times].filter(Boolean).join(' · ');
}

function Chip({ on, onClick, children, testId, color = RED }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      style={{ fontSize: 12, padding: '5px 12px', borderRadius: 999, cursor: 'pointer',
        border: `1px solid ${on ? color : '#2A2A2A'}`, background: on ? color + '18' : 'transparent', color: on ? color : '#a3a3a3' }}>
      {children}
    </button>
  );
}

// 2026-09-30 — "someone wants to try the machine": free-trial codes on the spot. HighMark only
// (this lives on the super-admin page; the server refuses courts). Valid 7 days. One code for a
// walk-in, or a handful for a court (owner, 2026-10-01: "once those are used, I won't give them
// new again") - pinned to that court's machine so they can't be used elsewhere.
export function GiveTrialModal({ mint, devices = [], onClose, onDone }) {
  const [minutes, setMinutes] = useState(15);
  const [who, setWho] = useState('');
  const [qty, setQty] = useState(1);
  const [deviceId, setDeviceId] = useState('');            // '' = any kiosk
  const [saving, setSaving] = useState(false);
  const [codes, setCodes] = useState([]);
  const code = codes[0] || '';
  const count = Math.min(20, Math.max(1, Math.floor(Number(qty) || 1)));
  const machine = devices.find(d => d.kiosk_id === deviceId);
  const give = async () => {
    setSaving(true);
    try {
      const today = ymd();
      const [y, m, d] = today.split('-').map(Number);
      const r = await mint({
        channel: 'kiosk', type: 'trial', value: minutes, quantity: count, count, prefix: 'TRY', code_length: 5,
        valid_from: today, valid_until: ymd(new Date(y, m - 1, d + 7)),
        funding: 'shared', court_pct: 100, owner_pct: 0, min_booked_minutes: 0, court_booking_min_minutes: 0,
        reason: (who.trim() ? `Trial for ${who.trim()}` : 'Free trial').slice(0, 80),
        device_ids: deviceId ? [deviceId] : [], site_names: [],
      });
      const got = r.codes || [];
      setCodes(got);
      toast.success(`${got.length} free trial code${got.length === 1 ? '' : 's'} ready: ${minutes} min`);
    } catch (e) { toast.error(errMsg(e, 'Could not make the trial code')); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title={code ? (codes.length > 1 ? `${codes.length} free trial codes` : 'Free trial code') : 'Give a free trial'} onClose={code ? onDone : onClose} testId="kiosk-give-trial">
      {code ? (
        <div>
          <p style={{ fontSize: 12, color: GREY }}>
            {minutes} min on the machine at ₱0 each. One use per code, valid 7 days, {machine ? `only on ${machine.name}${machine.court_name ? ` (${machine.court_name})` : ''}` : 'any kiosk'} running app 0.20 or newer.
          </p>
          <div style={{ marginTop: 12, background: '#0A0A0A', border: `1.5px solid ${RED}`, borderRadius: 12, padding: 16, textAlign: 'center', maxHeight: 260, overflowY: 'auto' }}>
            {codes.map(c => (
              <p key={c} data-testid={codes.length === 1 ? 'kiosk-give-trial-code' : `kiosk-give-trial-code-${c}`}
                style={{ fontFamily: 'Oswald, sans-serif', fontSize: codes.length === 1 ? 30 : 20, fontWeight: 900, letterSpacing: 2, color: '#fff', lineHeight: 1.5 }}>{c}</p>
            ))}
          </div>
          <button type="button" onClick={() => { navigator.clipboard?.writeText(codes.join('\n')); toast.success('Copied'); }} data-testid="kiosk-give-trial-copy"
            style={{ marginTop: 10, width: '100%', background: 'transparent', color: '#a3a3a3', border: '1px solid #2A2A2A', borderRadius: 10, padding: 10, cursor: 'pointer', fontSize: 13 }}>
            <Copy className="h-3.5 w-3.5 inline" /> {codes.length > 1 ? 'Copy all the codes' : 'Copy the code'}</button>
          <button type="button" onClick={onDone} style={{ marginTop: 10, width: '100%', background: RED, color: '#fff', border: 'none', borderRadius: 10, padding: 12, fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', cursor: 'pointer' }}>Done</button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p style={{ ...labelStyle, marginBottom: 8 }}>How long?</p>
            <div className="flex gap-2 flex-wrap">
              {TRIAL_MINUTES.map(m => <Chip key={m} on={minutes === m} onClick={() => setMinutes(m)} testId={`kiosk-give-trial-${m}`}>{m} min</Chip>)}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label style={{ display: 'block' }}>
              <span style={labelStyle}>How many codes?</span>
              <input data-testid="kiosk-give-trial-qty" type="number" min="1" max="20" value={qty} onChange={e => setQty(e.target.value)}
                style={{ ...inputStyle, marginTop: 6 }} />
            </label>
            <label style={{ display: 'block' }}>
              <span style={labelStyle}>Which machine?</span>
              <select data-testid="kiosk-give-trial-device" value={deviceId} onChange={e => setDeviceId(e.target.value)} style={{ ...inputStyle, marginTop: 6 }}>
                <option value="">Any kiosk</option>
                {devices.map(d => <option key={d.kiosk_id} value={d.kiosk_id}>{d.name}{d.court_name ? ` · ${d.court_name}` : d.site_name ? ` · ${d.site_name}` : ''}</option>)}
              </select>
            </label>
          </div>
          <label style={{ display: 'block' }}>
            <span style={labelStyle}>For who? <span style={{ color: '#555' }}>(optional, a note for you)</span></span>
            <input data-testid="kiosk-give-trial-who" value={who} maxLength={40} onChange={e => setWho(e.target.value)} placeholder="e.g. Juan, walk-in"
              style={{ ...inputStyle, marginTop: 6 }} />
          </label>
          <p style={{ fontSize: 11, color: '#555' }}>{TRIAL_FUNDING_LINE}</p>
          <button type="button" onClick={give} disabled={saving} data-testid="kiosk-give-trial-go"
            style={{ width: '100%', background: RED, color: '#fff', border: 'none', borderRadius: 10, padding: 12, fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', cursor: 'pointer' }}>
            {saving ? 'Making the codes…' : count > 1 ? `Make ${count} codes of ${minutes} min` : `Make a ${minutes}-minute code`}</button>
        </div>
      )}
    </ModalShell>
  );
}

export default function KioskVoucherWizard({ mode = 'platform', devices = [], mint, onClose, onDone }) {
  const tenant = mode === 'tenant';
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [f, setF] = useState({
    type: 'flat', value: '', min_booked_minutes: 60, reason: '',
    court_gate: false, court_min_hours: 3, // K6 P4b — sent as court_booking_min_minutes (hours × 60), 0 when off
    funding: tenant ? 'court' : 'shared', // K6 P4d — nobody is charged unless the minter says so
    court_pct: 100, scope: tenant ? 'devices' : 'any', device_ids: tenant ? devices.map(d => d.kiosk_id) : [], site_names: [],
    quantity: 20, prefix: tenant ? 'COURT' : 'BALL', code_length: 5, valid_from: ymd(), valid_until: '', label: '',
  });
  const set = (k) => (v) => setF(p => ({ ...p, [k]: v }));
  const toggle = (k, val) => setF(p => ({ ...p, [k]: p[k].includes(val) ? p[k].filter(x => x !== val) : [...p[k], val] }));
  const sites = useMemo(() => [...new Set(devices.map(d => d.site_name).filter(Boolean))], [devices]);
  const typeMeta = KIOSK_VOUCHER_TYPES.find(t => t.id === f.type);
  const courtPct = Math.min(100, Math.max(0, Number(f.court_pct) || 0));
  const ownerPct = 100 - courtPct;

  // K6 P4c — a bonus batch gives minutes, not money: the value field is "Free minutes"
  // (whole minutes, 1..240) and step 2's funding block does not apply to it.
  const bonus = f.type === 'bonus_time';
  const trial = f.type === 'trial';            // 2026-09-30: minutes of trial, 5–60, no booking, no money
  const valueNum = Number(f.value);
  const valueLabel = f.type === 'percent' ? 'Percent off (%)' : trial ? 'Trial minutes' : (f.type === 'free_time' || bonus) ? 'Free minutes' : 'Amount off (₱)';
  const valueOk = trial
    ? Number.isInteger(valueNum) && valueNum >= 5 && valueNum <= 60
    : bonus
    ? Number.isInteger(valueNum) && valueNum >= 1 && valueNum <= 240
    : valueNum > 0 && (f.type !== 'percent' || valueNum <= 90) && (f.type !== 'free_time' || valueNum <= 240);
  // K6 P4b — the court minimum in whole minutes; the gate needs 0.5–24 h to move on.
  const courtMinHours = Number(f.court_min_hours);
  const courtMinMinutes = f.court_gate ? Math.round(courtMinHours * 60) : 0;
  const courtGateOk = !f.court_gate || (courtMinHours >= 0.5 && courtMinHours <= 24);
  // K6 P4d — the mode that actually ships: a tenant may only mint court-funded batches,
  // and a bonus discounts nothing, so it rides along as 'shared' (nobody absorbs anything).
  // The percentages are still sent — placeholders for every mode but 'custom'.
  const fundingMode = tenant ? 'court' : (bonus || trial) ? 'shared' : f.funding;
  const fundingPct = fundingMode === 'custom' ? { court_pct: courtPct, owner_pct: ownerPct }
    : fundingMode === 'owner' ? { court_pct: 0, owner_pct: 100 }
      : { court_pct: 100, owner_pct: 0 };
  const canNext1 = valueOk && f.reason.trim().length <= 80 && courtGateOk;
  const scopeOk = f.scope === 'any' || (f.scope === 'devices' ? f.device_ids.length > 0 : f.site_names.length > 0);
  const canNext2 = scopeOk;
  const canGenerate = Number(f.quantity) >= 1 && Number(f.quantity) <= 500 && f.prefix.trim() && f.valid_until && f.valid_until >= f.valid_from;

  const payload = () => ({
    channel: 'kiosk',
    type: f.type, value: Number(f.value),
    min_booked_minutes: trial ? 0 : (Number(f.min_booked_minutes) || 0),
    court_booking_min_minutes: courtMinMinutes,
    reason: f.reason.trim(),
    // K6 P4c — nobody funds a bonus, but the server model still wants a split that adds to 100.
    // K6 P4d — `funding` is the truth; the two percentages ride along (placeholders unless custom).
    funding: fundingMode, ...fundingPct,
    device_ids: f.scope === 'devices' ? f.device_ids : [],
    site_names: f.scope === 'sites' ? f.site_names : [],
    quantity: Number(f.quantity), count: Number(f.quantity), prefix: f.prefix.trim().toUpperCase(), code_length: Number(f.code_length),
    valid_from: f.valid_from, valid_until: f.valid_until, label: f.label.trim() || undefined,
  });

  const generate = async () => {
    setSaving(true);
    try {
      const r = await mint(payload());
      setResult(r);
      if (r.count < Number(f.quantity)) toast.warning(`Generated ${r.count} of ${f.quantity} (some codes collided). You can mint more.`);
      else toast.success(`Generated ${r.count} ball machine vouchers`);
    } catch (e) { toast.error(errMsg(e, 'Generation failed')); }
    finally { setSaving(false); }
  };

  const title = result ? 'Vouchers ready' : `Ball machine vouchers · Step ${step}/3`;
  return (
    <ModalShell title={title} onClose={onClose} testId="kiosk-voucher-wizard">
      {result ? (
        <div>
          <div className="flex items-center gap-2 mb-3" style={{ color: GREEN }}>
            <CheckCircle className="h-5 w-5" /> <span style={{ fontWeight: 700 }}>{result.count} unique codes minted</span>
          </div>
          <div style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 10, padding: 12, maxHeight: 220, overflowY: 'auto' }} data-testid="kiosk-voucher-codes">
            {result.codes.map(c => (
              <div key={c} className="flex items-center justify-between" style={{ padding: '4px 0' }}>
                <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: 14, letterSpacing: 0.5 }}>{c}</span>
                <button type="button" onClick={() => { navigator.clipboard?.writeText(c); toast.success('Copied'); }} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer' }}><Copy className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => { navigator.clipboard?.writeText(result.codes.join('\n')); toast.success('All codes copied'); }} data-testid="kiosk-voucher-copy-all"
            style={{ marginTop: 10, width: '100%', background: 'transparent', color: '#a3a3a3', border: '1px solid #2A2A2A', borderRadius: 10, padding: 10, cursor: 'pointer', fontSize: 13 }}>Copy all codes</button>
          <button type="button" onClick={onDone} data-testid="kiosk-voucher-wizard-finish"
            style={{ marginTop: 10, width: '100%', background: RED, color: '#fff', border: 'none', borderRadius: 10, padding: 12, fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', cursor: 'pointer' }}>Done</button>
        </div>
      ) : (
        <>
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <p style={{ ...labelStyle, marginBottom: 8 }}>Discount</p>
                {/* Owner, 2026-10-01: courts may discount up to 90%, but they must see what it means - and that a
                    shared-cost promo is made WITH HighMark, not alone. */}
                {tenant && (
                  <div data-testid="kiosk-voucher-court-note"
                    style={{ background: AMBER + '12', border: `1px solid ${AMBER}55`, borderRadius: 10, padding: '10px 12px', marginBottom: 10 }}>
                    <p style={{ fontSize: 12, color: '#fff', fontWeight: 700 }}>The discount comes out of your court&apos;s share.</p>
                    <p style={{ fontSize: 11, color: '#cfcfcf', marginTop: 3, lineHeight: 1.5 }}>
                      HighMark&apos;s share is counted on the full price, so a big discount can leave your court owing HighMark at settlement.
                      Planning a promo together? <b style={{ color: AMBER }}>Contact HighMark</b> and we&apos;ll make the voucher with you and share the cost.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {KIOSK_VOUCHER_TYPES.filter(t => !(tenant && t.platformOnly)).map(t => (
                    <button key={t.id} type="button" onClick={() => set('type')(t.id)} data-testid={`kiosk-voucher-type-${t.id}`}
                      style={{ textAlign: 'left', padding: 10, borderRadius: 12, cursor: 'pointer',
                        border: `1.5px solid ${f.type === t.id ? RED : '#2A2A2A'}`, background: f.type === t.id ? RED + '14' : '#0A0A0A' }}>
                      <t.Icon className="h-4 w-4 mb-1" style={{ color: f.type === t.id ? RED : GREY }} />
                      <p style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{t.label}</p>
                      <p style={{ fontSize: 10, color: GREY, marginTop: 2 }}>{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <label style={{ display: 'block' }}>
                <span style={labelStyle}>{valueLabel} *</span>
                <input data-testid="kiosk-voucher-value" type="number" min="0" value={f.value} onChange={e => set('value')(e.target.value)}
                  placeholder={f.type === 'percent' ? 'e.g. 10' : trial ? 'e.g. 15' : (f.type === 'free_time' || bonus) ? 'e.g. 30' : 'e.g. 100'} style={{ ...inputStyle, marginTop: 6 }} />
                {f.type === 'free_time' && <span style={{ fontSize: 11, color: '#555' }}>Priced at the session&apos;s own rate (price ÷ booked hours), never more than 90% of the price.</span>}
                {/* 2026-10-01: a discount never makes a rental free — free play is a Free trial (rain-fair). */}
                {(f.type === 'flat' || f.type === 'percent') && (
                  <span style={{ fontSize: 11, color: f.type === 'percent' && valueNum > 90 ? RED : '#555', display: 'block' }} data-testid="kiosk-voucher-cap-note">
                    At most 90% off the rental price.{tenant ? '' : ' For free play, use a Free trial.'}
                  </span>
                )}
                {/* K6 P4c — minutes on the clock, never money off: whole minutes only. */}
                {bonus && <span style={{ fontSize: 11, color: '#555' }}>Extra minutes on the clock, at no charge. Whole minutes, 1 to 240.</span>}
                {trial && (
                  <div className="flex gap-2 flex-wrap mt-2" data-testid="kiosk-voucher-trial-presets">
                    {TRIAL_MINUTES.map(m => <Chip key={m} on={valueNum === m} onClick={() => set('value')(String(m))} testId={`kiosk-voucher-trial-${m}`}>{m} min</Chip>)}
                  </div>
                )}
                {trial && <span style={{ fontSize: 11, color: '#555', display: 'block', marginTop: 4 }}>The whole session, at ₱0 — no paid booking needed. 5 to 60 minutes. One code, one try.</span>}
              </label>
              {!trial && <div>
                <span style={labelStyle}>Minimum booking</span>
                <div className="flex gap-2 flex-wrap mt-2 items-center">
                  {MIN_PRESETS.map(([m, l]) => <Chip key={m} on={Number(f.min_booked_minutes) === m} onClick={() => set('min_booked_minutes')(m)} testId={`kiosk-voucher-min-${m}`}>{l}</Chip>)}
                  <input data-testid="kiosk-voucher-min-minutes" type="number" min="0" max="1440" value={f.min_booked_minutes} onChange={e => set('min_booked_minutes')(e.target.value)}
                    style={{ ...inputStyle, width: 90, padding: '5px 8px', fontSize: 12 }} /> <span style={{ fontSize: 12, color: GREY }}>min</span>
                </div>
                <span style={{ fontSize: 11, color: '#555' }}>Fixed bookings only. A problem stop releases the code even if the minimum was met.</span>
              </div>}
              {/* K6 P4b — the court-booking gate: on → the voucher only applies while a court booking is being played. */}
              <div style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 10, padding: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input data-testid="kiosk-voucher-court-gate" type="checkbox" checked={f.court_gate} onChange={e => set('court_gate')(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: RED, cursor: 'pointer' }} />
                  <span style={{ fontSize: 13, color: '#fff', fontWeight: 700 }}>Needs an active court booking</span>
                </label>
                <p style={{ fontSize: 11, color: '#555', marginTop: 4 }}>The customer must be playing a court booking at the linked court right now; staff confirm it with the staff PIN on the kiosk.</p>
                {f.court_gate && (
                  <label style={{ display: 'block', marginTop: 10 }}>
                    <span style={labelStyle}>Minimum court booking (hours)</span>
                    <div className="flex items-center gap-2" style={{ marginTop: 6 }}>
                      <input data-testid="kiosk-voucher-court-min-hours" type="number" step="0.5" min="0.5" max="24" value={f.court_min_hours}
                        onChange={e => set('court_min_hours')(e.target.value)} style={{ ...inputStyle, width: 110, padding: '5px 8px', fontSize: 12 }} />
                      <span style={{ fontSize: 12, color: GREY }}>h · {courtGateOk ? courtGateLabel(courtMinMinutes) : 'between 0.5 and 24 hours'}</span>
                    </div>
                  </label>
                )}
              </div>
              <label style={{ display: 'block' }}>
                <span style={labelStyle}>Reason <span style={{ color: '#555' }}>({f.reason.length}/80)</span></span>
                <input data-testid="kiosk-voucher-reason" value={f.reason} maxLength={80} onChange={e => set('reason')(e.target.value)}
                  placeholder={tenant ? 'e.g. Members’ week' : 'e.g. Launch promo, Court A'} style={{ ...inputStyle, marginTop: 6 }} />
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              {/* K6 P4c — a bonus has no funding step: the free time is nobody's cost. */}
              {(bonus || trial) ? (
                <div style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 10, padding: 12 }} data-testid={trial ? 'kiosk-voucher-funding-trial' : 'kiosk-voucher-funding-bonus'}>
                  <p style={{ fontSize: 13, color: '#fff', fontWeight: 700 }}>{trial ? 'Free trial' : 'Free machine time'}</p>
                  <p style={{ fontSize: 12, color: GREY, marginTop: 4 }}>{trial ? TRIAL_FUNDING_LINE : BONUS_FUNDING_LINE}</p>
                </div>
              ) : (
              <div>
                {/* K6 P4d — the four modes. The default charges nobody; only "Custom split" shows percentages. */}
                <p style={{ ...labelStyle, marginBottom: 8 }}>Who covers the discount?</p>
                {tenant ? (
                  <div style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 10, padding: 12 }} data-testid="kiosk-voucher-funding-tenant">
                    <p style={{ fontSize: 13, color: '#fff', fontWeight: 700 }}>Court-funded — 100 %</p>
                    <p style={{ fontSize: 12, color: GREY, marginTop: 4 }}>The discount comes out of your share; HighMark&apos;s share of the full price is unchanged.</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {FUNDING_CHOICES.map(c => (
                        <button key={c.id} type="button" onClick={() => set('funding')(c.id)} data-testid={`kiosk-voucher-funding-${c.id}`}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: 10, borderRadius: 12, cursor: 'pointer',
                            border: `1.5px solid ${f.funding === c.id ? RED : '#2A2A2A'}`, background: f.funding === c.id ? RED + '14' : '#0A0A0A' }}>
                          <p style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{c.label}</p>
                          {c.note && <p style={{ fontSize: 11, color: GREY, marginTop: 2 }}>{c.note}</p>}
                        </button>
                      ))}
                    </div>
                    {f.funding === 'custom' && (
                      <div style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 10, padding: 12, marginTop: 10 }} data-testid="kiosk-voucher-funding-custom-split">
                        <input type="range" min="0" max="100" step="5" value={courtPct} onChange={e => set('court_pct')(Number(e.target.value))} data-testid="kiosk-voucher-split-slider" style={{ width: '100%' }} />
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <label>
                            <span style={labelStyle}>Court %</span>
                            <input data-testid="kiosk-voucher-court-pct" type="number" min="0" max="100" value={courtPct} onChange={e => set('court_pct')(Number(e.target.value))} style={{ ...inputStyle, marginTop: 6 }} />
                          </label>
                          <label>
                            <span style={labelStyle}>HighMark %</span>
                            <input data-testid="kiosk-voucher-owner-pct" type="number" min="0" max="100" value={ownerPct} onChange={e => set('court_pct')(100 - Number(e.target.value))} style={{ ...inputStyle, marginTop: 6 }} />
                          </label>
                        </div>
                        <p style={{ fontSize: 12, color: AMBER, marginTop: 8, fontWeight: 700 }}>{fundingLabel({ funding: 'custom', court_pct: courtPct })}</p>
                        <p style={{ fontSize: 11, color: '#555', marginTop: 2 }}>HighMark&apos;s side of a rental = its share of the full price minus its part of the discount; the court keeps the rest of the cash.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
              )}
              <div>
                <p style={{ ...labelStyle, marginBottom: 8 }}>Where it can be used</p>
                {!tenant && (
                  <div className="flex gap-2 flex-wrap mb-3">
                    <Chip on={f.scope === 'any'} onClick={() => set('scope')('any')} testId="kiosk-voucher-scope-any">Any kiosk</Chip>
                    <Chip on={f.scope === 'devices'} onClick={() => set('scope')('devices')} testId="kiosk-voucher-scope-devices">Selected phones</Chip>
                    <Chip on={f.scope === 'sites'} onClick={() => set('scope')('sites')} testId="kiosk-voucher-scope-sites">By site name</Chip>
                  </div>
                )}
                {f.scope === 'devices' && (
                  <div className="flex gap-2 flex-wrap" data-testid="kiosk-voucher-devices">
                    {devices.map(d => (
                      <Chip key={d.kiosk_id} on={f.device_ids.includes(d.kiosk_id)} onClick={() => toggle('device_ids', d.kiosk_id)} testId={`kiosk-voucher-device-${d.kiosk_id}`}>
                        {d.name}{d.site_name ? ` · ${d.site_name}` : ''}
                      </Chip>
                    ))}
                    {devices.length === 0 && <span style={{ fontSize: 12, color: '#555' }}>{tenant ? 'No rental kiosk is linked to your court.' : 'No phones paired yet.'}</span>}
                  </div>
                )}
                {f.scope === 'sites' && (
                  <div className="flex gap-2 flex-wrap" data-testid="kiosk-voucher-sites">
                    {sites.map(s => <Chip key={s} on={f.site_names.includes(s)} onClick={() => toggle('site_names', s)} testId={`kiosk-voucher-site-${s}`}>{s}</Chip>)}
                    {sites.length === 0 && <span style={{ fontSize: 12, color: '#555' }}>No site names set on the phones yet.</span>}
                  </div>
                )}
                {tenant && <p style={{ fontSize: 11, color: '#555', marginTop: 6 }}>Only the phones linked to your court. Leave all selected to allow every one of them.</p>}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <label>
                  <span style={labelStyle}>How many? *</span>
                  <input data-testid="kiosk-voucher-quantity" type="number" min="1" max="500" value={f.quantity} onChange={e => set('quantity')(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
                </label>
                <label>
                  <span style={labelStyle}>Code length</span>
                  <select data-testid="kiosk-voucher-code-length" value={f.code_length} onChange={e => set('code_length')(e.target.value)} style={{ ...inputStyle, marginTop: 6, cursor: 'pointer' }}>
                    <option value={4}>4 characters</option>
                    <option value={5}>5 characters</option>
                  </select>
                </label>
              </div>
              <label style={{ display: 'block' }}>
                <span style={labelStyle}>Prefix *</span>
                <input data-testid="kiosk-voucher-prefix" value={f.prefix} onChange={e => set('prefix')(e.target.value.toUpperCase())} placeholder="BALL" style={{ ...inputStyle, marginTop: 6 }} />
                <span style={{ fontSize: 11, color: '#555' }}>Codes look like <b style={{ color: '#a3a3a3' }}>{(f.prefix || 'VCH').toUpperCase()}-{'X'.repeat(Number(f.code_length))}</b></span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label>
                  <span style={labelStyle}>Valid from</span>
                  <input data-testid="kiosk-voucher-valid-from" type="date" value={f.valid_from} onChange={e => set('valid_from')(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
                </label>
                <label>
                  <span style={labelStyle}>Expiry *</span>
                  <input data-testid="kiosk-voucher-valid-until" type="date" value={f.valid_until} min={f.valid_from} onChange={e => set('valid_until')(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
                </label>
              </div>
              {!tenant && (
                <label style={{ display: 'block' }}>
                  <span style={labelStyle}>Batch label</span>
                  <input data-testid="kiosk-voucher-label" value={f.label} maxLength={60} onChange={e => set('label')(e.target.value)} placeholder="optional, for the list" style={{ ...inputStyle, marginTop: 6 }} />
                </label>
              )}
              <div style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 10, padding: 12 }} data-testid="kiosk-voucher-review">
                <p style={{ ...labelStyle, marginBottom: 6 }}>Review</p>
                {/* K6 P4c — a bonus reads "Free machine time · 30 min", and no funding word: nobody funds it. */}
                <p style={{ fontSize: 13, color: '#fff' }}>{typeMeta?.label} · <b style={{ color: RED }}>{f.value}{f.type === 'percent' ? ' %' : (f.type === 'free_time' || bonus || trial) ? ' min' : ' ₱'}</b></p>
                <p style={{ fontSize: 12, color: GREY, marginTop: 3 }}>
                  {/* K6 P4d — the Review names the chosen mode; a custom split still reads as percentages. */}
                  {(bonus || trial) ? '' : `${fundingLabel({ funding: fundingMode, ...fundingPct })} · `}{trial ? 'no booking needed' : `min ${f.min_booked_minutes} min`} · {scopeLabel({ device_ids: f.scope === 'devices' ? f.device_ids : [], site_names: f.scope === 'sites' ? f.site_names : [] }, f.scope === 'devices' ? f.device_ids.map(id => devices.find(d => d.kiosk_id === id)?.name || id) : [])}
                </p>
                {courtMinMinutes > 0 && <p style={{ fontSize: 12, color: AMBER, marginTop: 3 }} data-testid="kiosk-voucher-review-court-gate">{courtGateLabel(courtMinMinutes)}</p>}
                <p style={{ fontSize: 12, color: GREY, marginTop: 3 }}>{f.quantity} codes · expires {f.valid_until || '—'}{f.reason ? ` · ${f.reason}` : ''}</p>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-6">
            {step > 1 && (
              <button type="button" onClick={() => setStep(step - 1)} data-testid="kiosk-voucher-wizard-back"
                style={{ flex: 1, background: 'transparent', color: '#a3a3a3', border: '1px solid #2A2A2A', borderRadius: 10, padding: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
            )}
            {step < 3 ? (
              <button type="button" onClick={() => setStep(step + 1)} disabled={step === 1 ? !canNext1 : !canNext2} data-testid="kiosk-voucher-wizard-next"
                style={{ flex: 2, background: RED, color: '#fff', border: 'none', borderRadius: 10, padding: 11, fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif',
                  cursor: (step === 1 ? !canNext1 : !canNext2) ? 'not-allowed' : 'pointer', opacity: (step === 1 ? !canNext1 : !canNext2) ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                Next <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button type="button" onClick={generate} disabled={!canGenerate || saving} data-testid="kiosk-voucher-generate-btn"
                style={{ flex: 2, background: GREEN, color: '#000', border: 'none', borderRadius: 10, padding: 11, fontWeight: 800, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif',
                  cursor: (!canGenerate || saving) ? 'not-allowed' : 'pointer', opacity: (!canGenerate || saving) ? 0.5 : 1 }}>
                {saving ? 'Generating…' : 'Generate vouchers'}
              </button>
            )}
          </div>
        </>
      )}
      <span style={{ display: 'none' }}><X /></span>
    </ModalShell>
  );
}

// Badge colours for the kiosk voucher states (shared by both lists).
export const KIOSK_STATUS = {
  unused: { c: GREEN, t: 'Unused' }, claimed: { c: AMBER, t: 'Claimed' }, used: { c: '#737373', t: 'Used' },
  released: { c: '#4FC3F7', t: 'Released' }, expired: { c: '#FF2D55', t: 'Expired' }, scheduled: { c: '#FFB300', t: 'Scheduled' },
};

export function KioskStatusBadge({ status }) {
  const s = KIOSK_STATUS[status] || KIOSK_STATUS.unused;
  return (
    <span data-testid={`kiosk-voucher-status-${status}`} style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5,
      color: s.c, background: s.c + '18', padding: '3px 8px', borderRadius: 999 }}>{s.t}</span>
  );
}
